import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { List, Typography, Dropdown, Modal, Button, Spin } from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import { getMyProfile } from "src/domain/app/redux/auth/auth-selectors";
import { ChatMessageDto } from "src/shared/services/chat/dto";
import { useChat } from "src/shared/hooks/useChat";
import MessageAttachments from "../MessageAttachments";
import moment from "moment";
import "./styles.less";

const { Text, Paragraph } = Typography;

interface MessageListProps {
  messages: ChatMessageDto[];
  roomId: string;
}

const MessageList: React.FC<MessageListProps> = ({ messages, roomId }) => {
  const { messages: intlMessages } = useIntl();
  const { deleteMessage, loadMessages } = useChat();
  const myProfile = useSelector(getMyProfile);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
  }, [roomId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleScroll = async () => {
    if (!messagesContainerRef.current || !hasMore || isLoadingMore) return;

    const { scrollTop } = messagesContainerRef.current;

    if (scrollTop === 0) {
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      await loadMessages(roomId, nextPage);
      setCurrentPage(nextPage);
      setIsLoadingMore(false);

      // If we got less than 50 messages, we've reached the end
      if (messages.length % 50 !== 0) {
        setHasMore(false);
      }
    }
  };

  const handleCopyMessage = (content: string) => {
    navigator.clipboard.writeText(content);
    Modal.success({
      content: intlMessages["chat.messageCopied"] as string,
    });
  };

  const handleDeleteMessage = (messageId: string) => {
    Modal.confirm({
      title: intlMessages["chat.deleteMessageConfirm"] as string,
      onOk: () => deleteMessage(messageId),
    });
  };

  const getMenuItems = (message: ChatMessageDto) => {
    const items = [
      {
        key: "copy",
        label: intlMessages["chat.copy"] as string,
        icon: <CopyOutlined />,
        onClick: () => handleCopyMessage(message.message),
        danger: false,
      },
    ];

    if (message.senderId === myProfile?.id) {
      items.push({
        key: "delete",
        label: intlMessages["chat.delete"] as string,
        icon: <DeleteOutlined />,
        onClick: () => handleDeleteMessage(message.id),
        danger: true,
      });
    }

    return items;
  };

  const isMyMessage = (message: ChatMessageDto) =>
    message.senderId === myProfile?.id;

  const formatMessageTime = (dateStr: string) => {
    return moment(dateStr).format("hh:mm A");
  };

  return (
    <div
      className="message-list-container"
      ref={messagesContainerRef}
      onScroll={handleScroll}
    >
      {isLoadingMore && (
        <div className="load-more-indicator">
          <Spin size="small" />
        </div>
      )}

      {!hasMore && messages.length > 0 && (
        <div className="no-more-messages">
          <Text type="secondary">
            {intlMessages["chat.noMoreMessages"] as string}
          </Text>
        </div>
      )}

      <List
        className="message-list"
        dataSource={messages}
        renderItem={(message) => (
          <div
            className={`message-item ${isMyMessage(message) ? "my-message" : "other-message"}`}
          >
            <div className="message-content-wrapper">
              {!isMyMessage(message) && (
                <Text className="sender-name" strong>
                  {message.senderName}
                </Text>
              )}

              <Dropdown
                menu={{ items: getMenuItems(message) }}
                trigger={["contextMenu"]}
              >
                <div
                  className={`message-bubble ${message.isDeleted ? "deleted" : ""}`}
                >
                  {message.isDeleted ? (
                    <Text type="secondary" italic>
                      {intlMessages["chat.messageDeleted"] as string}
                    </Text>
                  ) : (
                    <>
                      {!!message.attachments?.length && (
                        <MessageAttachments attachments={message.attachments} />
                      )}
                      {message.message && (
                        <Paragraph className="message-text">
                          {message.message}
                        </Paragraph>
                      )}
                    </>
                  )}

                  <Text className="message-time">
                    {formatMessageTime(message.sentAt)}
                  </Text>
                </div>
              </Dropdown>
            </div>
          </div>
        )}
      />

      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
